import { Component, Injector } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ModelEditor } from 'src/app/shared/editors/modeleditor';
import { EscalationProviderSelectItemDto, NoticeTriggerGroupDto, NoticeTriggerGroupTriggerDto } from 'src/app/Services/enforcement.service';
import { ToastrService } from 'ngx-toastr';

type DocumentTemplateDto = { Id: string, Name: string, TargetType?: string | null };

@Component({
    selector: 'app-editnoticetriggerset',
    templateUrl: './editnoticetriggerset.component.html',
    styleUrls: ['./editnoticetriggerset.component.scss'],
    standalone: false
})
export class EditNoticeTriggerSetComponent extends ModelEditor {
  public templates: DocumentTemplateDto[] = [];
  public escalationProviders: EscalationProviderSelectItemDto[] = [];

  constructor(private injector: Injector, private toastr: ToastrService) {
    super("enforcement/noticetriggersets", injector);

    // Best-effort load; the page still works without template names.
    this.api.Get<any[]>('communications/documenttemplates').then(all => {
      this.templates = (all || [])
        .filter(t => (t?.TargetType || '').toString().toLowerCase() === 'citation')
        .map(t => ({ Id: t.Id, Name: t.Name, TargetType: t.TargetType }))
        .sort((a, b) => a.Name.localeCompare(b.Name));
    });

    // Load provider dropdown options (enabled for current org).
    this.api.Get<EscalationProviderSelectItemDto[]>('enforcement/escalationproviders')
      .then(items => {
        this.escalationProviders = (items || []).sort((a, b) => (a?.Name || '').localeCompare(b?.Name || ''));

        if (this.escalationProviders.length === 0) {
          this.toastr.error("No escalation providers are enabled for this organization. Please contact support.");
          return;
        }

        // If the model is already loaded and has no provider selected, default to first option.
        if (this.Model && !(this.Model as any).EscalationProviderType) {
          (this.Model as any).EscalationProviderType = this.escalationProviders[0].Id;
          this.Form?.markAsDirty();
        }
      })
      .catch(() => {
        this.toastr.error("Unable to load escalation providers. Please try again or contact support.");
      });
  }

  public override DefaultModel(): NoticeTriggerGroupDto {
    return { Id: 0, Name: '', OnlyMostRecentNotice: true, EscalationProviderType: '', Triggers: [] };
  }

  public override Validators(): any {
    return {
      Name: [Validators.required, Validators.maxLength(255)],
      OnlyMostRecentNotice: [],
      EscalationProviderType: [Validators.required],
    };
  }

  public override AfterModelLoaded(): void {
    if(!this.Model) return;
    const m = this.Model as NoticeTriggerGroupDto;
    m.Triggers = (m.Triggers || []).sort((a, b) => a.Index - b.Index);
    this.reindexTriggers();

    if (!m.EscalationProviderType) {
      // Default to the first available provider, otherwise fall back to frog.
      m.EscalationProviderType = this.escalationProviders?.[0]?.Id ?? 'frog';
    }
  }

  public override BeforeSave(): boolean | void {
    if(!this.Model) return;
    const m = this.Model as NoticeTriggerGroupDto;

    // Ensure stable indices before saving.
    this.reindexTriggers();

    // The triggers UI edits `Model.Triggers` via standalone ngModel, not the reactive form.
    // Sync `Model.Triggers` into the form so the base ModelEditor.Save() submits correct data.
    const fa = new UntypedFormArray([]);
    for(const t of (m.Triggers || []))
    {
      fa.push(new UntypedFormGroup({
        Id: new UntypedFormControl(t.Id),
        Index: new UntypedFormControl(t.Index),
        DaysAfterCreated: new UntypedFormControl(t.DaysAfterCreated),
        DocumentTemplateId: new UntypedFormControl(t.DocumentTemplateId),
        ActionType: new UntypedFormControl((t as any).ActionType ?? 0),
        IsFinalNotice: new UntypedFormControl(false),
        FinalNoticeDueDays: new UntypedFormControl(30),
      }));
    }
    this.Form.setControl('Triggers', fa);
  }

  public addTrigger(): void {
    if(!this.Model) return;
    const m = this.Model as NoticeTriggerGroupDto;
    m.Triggers = [
      ...(m.Triggers || []),
      { Id: 0, Index: (this.Model.Triggers?.length ?? 0), DaysAfterCreated: 0, DocumentTemplateId: null, ActionType: 0 } as any
    ];
    this.reindexTriggers();
    this.Form.markAsDirty();
  }

  public removeTrigger(i: number): void {
    if(!this.Model) return;
    const m = this.Model as NoticeTriggerGroupDto;
    m.Triggers.splice(i, 1);
    m.Triggers = [...m.Triggers];
    this.reindexTriggers();
    this.Form.markAsDirty();
  }

  public templateName(id: string): string {
    const t = this.templates.find(x => x.Id?.toLowerCase() === (id || '').toLowerCase());
    return t ? t.Name : '';
  }

  public getEscalationProviderName(): string {
    const provider = this.escalationProviders.find(x => x.Id === this.Form.get('EscalationProviderType')?.value);
    return provider ? provider.Name : '';
  }

  public triggerActionValue(t: any): string {
    if ((t as any)?.ActionType === 1) return 'raise';
    const id = (t as any)?.DocumentTemplateId;
    return id ? `template:${id}` : '';
  }

  public setTriggerActionFromValue(t: any, value: string): void {
    if (!t) return;
    if ((value || '').toLowerCase() === 'raise') {
      t.ActionType = 1;
      t.DocumentTemplateId = null;
    } else if ((value || '').startsWith('template:')) {
      t.ActionType = 0;
      t.DocumentTemplateId = (value || '').substring('template:'.length);
    } else {
      t.ActionType = 0;
      t.DocumentTemplateId = null;
    }

    // Ensure only one "raise" trigger exists by keeping the most recently edited one.
    const m = this.Model as any;
    const raises = (m?.Triggers || []).filter((x: any) => x?.ActionType === 1);
    if (raises.length > 1) {
      (m?.Triggers || []).forEach((x: any) => {
        if (x !== t && x?.ActionType === 1) {
          x.ActionType = 0;
        }
      });
    }

    this.Form.markAsDirty();
  }

  public moveTriggerUp(index: number): void {
    if(!this.Model) return;
    const m = this.Model as NoticeTriggerGroupDto;
    if(index <= 0) return;
    const tmp = m.Triggers[index - 1];
    m.Triggers[index - 1] = m.Triggers[index];
    m.Triggers[index] = tmp;
    this.reindexTriggers();
    this.Form.markAsDirty();
  }

  public moveTriggerDown(index: number): void {
    if(!this.Model) return;
    const m = this.Model as NoticeTriggerGroupDto;
    if(index < 0 || index >= m.Triggers.length - 1) return;
    const tmp = m.Triggers[index + 1];
    m.Triggers[index + 1] = m.Triggers[index];
    m.Triggers[index] = tmp;
    this.reindexTriggers();
    this.Form.markAsDirty();
  }

  private reindexTriggers(): void {
    if(!this.Model) return;
    const m = this.Model as NoticeTriggerGroupDto;
    if(!m.Triggers) m.Triggers = [];
    m.Triggers.forEach((t, idx) => { t.Index = idx; });
  }
}
